import { useEffect, useState } from "react";
import { Certificate, Plus, PencilSimple, Trash, UsersThree, FilePdf } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  TIPI_ATTIVITA_FORMATIVA, DESTINATARI_FORMAZIONE, METODI_VERIFICA_EFFICACIA, ESITI_VERIFICA, RIFERIMENTI_TIPO,
} from "@/lib/constants";
import { toast } from "sonner";

const emptyForm = () => ({
  tenant_id: "", pianificata: true, finalita: "", oggetto: "", esempi: "", durata: "",
  tipo_attivita: [], destinatari: [], riferimenti_tipo: "", docente_nome: "", docente_mansione: "",
  partecipanti: [], responsabile_esecuzione: "", data: "", scadenza_aggiornamento: "", verifica_efficacia: [],
  esito_verifica: "", vnc_numero: "", data_verifica: "", valutatore: "", note: "",
});

export default function TrainingSessions() {
  const { tenantId, organizations, currentOrg } = useTenant();
  const { user } = useAuth();
  const isConsultant = user?.role === "consultant";
  const canWrite = user?.role === "consultant" || user?.role === "client_user";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [emps, setEmps] = useState([]);
  const [manual, setManual] = useState({ nome: "", mansione: "" });
  const [pdfLoading, setPdfLoading] = useState(null);
  const [view, setView] = useState("list");
  const [calMonth, setCalMonth] = useState(new Date());
  const [calDay, setCalDay] = useState(null);

  const load = () => {
    setLoading(true);
    const p = tenantId ? `?tenant_id=${tenantId}` : "";
    api.get(`/training-sessions${p}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [tenantId]);

  const loadEmps = (tid) => {
    if (!tid) { setEmps([]); return; }
    api.get(`/employees?tenant_id=${tid}`).then((r) => setEmps(r.data)).catch(() => setEmps([]));
  };

  const openNew = () => {
    setEditingId(null);
    const tid = tenantId || "";
    setForm({ ...emptyForm(), tenant_id: tid });
    loadEmps(tid); setManual({ nome: "", mansione: "" }); setOpen(true);
  };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ ...emptyForm(), ...s, partecipanti: (s.partecipanti || []).map((p) => ({ ...p })) });
    loadEmps(s.tenant_id); setManual({ nome: "", mansione: "" }); setOpen(true);
  };

  const toggleArr = (key, val) => setForm((f) => {
    const arr = f[key] || [];
    return { ...f, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const addEmployee = (eid) => {
    const e = emps.find((x) => x.id === eid);
    if (!e) return;
    if ((form.partecipanti || []).some((p) => p.employee_id === eid)) return;
    setForm((f) => ({ ...f, partecipanti: [...f.partecipanti, { employee_id: e.id, nome: e.nome, mansione: e.ruolo || "" }] }));
  };
  const addManual = () => {
    if (!manual.nome.trim()) return;
    setForm((f) => ({ ...f, partecipanti: [...f.partecipanti, { employee_id: null, nome: manual.nome.trim(), mansione: manual.mansione.trim() }] }));
    setManual({ nome: "", mansione: "" });
  };
  const removePart = (i) => setForm((f) => ({ ...f, partecipanti: f.partecipanti.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.tenant_id) { toast.error("Seleziona un cliente"); return; }
    if (!form.oggetto.trim()) { toast.error("Inserisci l'oggetto dell'attività"); return; }
    const payload = { ...form };
    delete payload.id; delete payload.created_at;
    try {
      if (editingId) { await api.put(`/training-sessions/${editingId}`, payload); toast.success("Scheda aggiornata"); }
      else { await api.post("/training-sessions", payload); toast.success("Scheda creata"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questa scheda? Verrà rimossa anche dalle schede dei dipendenti collegati.")) return;
    try { await api.delete(`/training-sessions/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const downloadPdf = async (s) => {
    setPdfLoading(s.id);
    try {
      const r = await api.get(`/training-sessions/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Scheda_Attivita_Formativa_${(s.oggetto || "scheda").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(apiError(e)); }
    finally { setPdfLoading(null); }
  };
  const orgName = (tid) => organizations.find((o) => o.id === tid)?.nome || "";

  const toDate = (v) => { try { return v ? new Date(v + "T00:00:00") : null; } catch { return null; } };
  const sessionDates = items.map((s) => toDate(s.data)).filter(Boolean);
  const updateDates = items.map((s) => toDate(s.scadenza_aggiornamento)).filter(Boolean);
  const dayStr = calDay ? `${calDay.getFullYear()}-${String(calDay.getMonth() + 1).padStart(2, "0")}-${String(calDay.getDate()).padStart(2, "0")}` : null;
  const dayItems = dayStr ? items.filter((s) => s.data === dayStr || s.scadenza_aggiornamento === dayStr) : items;

  const renderCard = (s) => (
    <Card key={s.id} data-testid={`session-${s.id}`} className="p-4 border-slate-200">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-slate-900">{s.oggetto}</h3>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
            {s.data && <span>{s.data}</span>}
            {s.durata && <span>· {s.durata}</span>}
            {s.docente_nome && <span>· Docente: {s.docente_nome}</span>}
            {s.scadenza_aggiornamento && <span>· agg. entro {s.scadenza_aggiornamento}</span>}
            {!tenantId && isConsultant && <span>· {orgName(s.tenant_id)}</span>}
            <span className="inline-flex items-center gap-1"><UsersThree size={13} /> {(s.partecipanti || []).length} partecipanti</span>
            {s.pianificata === false && <Badge variant="outline" className="text-[10px]">Non pianificata</Badge>}
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {(s.tipo_attivita || []).map((t, i) => <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>)}
            {s.esito_verifica === "SI" && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Verifica: idoneo</Badge>}
            {s.esito_verifica === "NO" && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">Verifica: non idoneo</Badge>}
            {s.esito_verifica === "VNC" && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">VNC{s.vnc_numero ? ` n° ${s.vnc_numero}` : ""}</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="outline" data-testid={`session-pdf-${s.id}`} onClick={() => downloadPdf(s)} disabled={pdfLoading === s.id}>
            {pdfLoading === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePdf size={16} className="mr-1.5" />} PDF
          </Button>
          {canWrite && <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-session-${s.id}`} onClick={() => openEdit(s)}><PencilSimple size={16} /></Button>}
          {canWrite && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" data-testid={`delete-session-${s.id}`} onClick={() => remove(s.id)}><Trash size={16} /></Button>}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      <PageHero label="MOD.PO04_02" title="Schede Attività Formativa"
        subtitle={tenantId ? currentOrg?.nome : "Sessioni formative con partecipanti, verifica efficacia ed export PDF"}>
        {canWrite && (
          <Button onClick={openNew} data-testid="new-session-btn"
            className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuova scheda
          </Button>
        )}
      </PageHero>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-slate-200">
          <Certificate size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nessuna scheda formativa{tenantId ? "" : ". Seleziona un cliente per aggiungerne"}.</p>
        </Card>
      ) : (
        <>
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-white">
            <Button size="sm" variant={view === "list" ? "default" : "ghost"} className={view === "list" ? "bg-slate-900 text-white hover:bg-slate-800" : ""} data-testid="view-list" onClick={() => setView("list")}>Elenco</Button>
            <Button size="sm" variant={view === "calendar" ? "default" : "ghost"} className={view === "calendar" ? "bg-slate-900 text-white hover:bg-slate-800" : ""} data-testid="view-calendar" onClick={() => {
              const all = sessionDates.concat(updateDates).sort((a, b) => a - b);
              const now = new Date(); now.setHours(0, 0, 0, 0);
              const next = all.find((d) => d >= now) || all[all.length - 1];
              if (next) setCalMonth(next);
              setView("calendar");
            }}>Calendario</Button>
          </div>

          {view === "calendar" ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="p-4 border-slate-200 h-fit">
                <Calendar mode="single" selected={calDay} onSelect={setCalDay} month={calMonth} onMonthChange={setCalMonth}
                  modifiers={{ sessione: sessionDates, aggiornamento: updateDates }}
                  modifiersClassNames={{ sessione: "bg-blue-600 text-white rounded-md font-semibold", aggiornamento: "ring-2 ring-amber-400 rounded-md" }}
                  className="rounded-md" />
                <div className="text-xs text-slate-500 mt-3 space-y-1 px-2">
                  <div className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-blue-600 inline-block" /> Data sessione formativa</div>
                  <div className="flex items-center gap-2"><span className="h-3 w-3 rounded ring-2 ring-amber-400 inline-block" /> Scadenza aggiornamento</div>
                </div>
                {calDay && <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setCalDay(null)}>Mostra tutte</Button>}
              </Card>
              <div className="lg:col-span-2 space-y-3" data-testid="sessions-list">
                {dayStr && <p className="text-sm text-slate-500">Schede del {dayStr}</p>}
                {dayItems.map(renderCard)}
                {dayItems.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Nessuna scheda in questa data.</p>}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="sessions-list">
              {items.map(renderCard)}
            </div>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Modifica scheda formativa" : "Nuova scheda attività formativa"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              {!editingId && (
                <div>
                  <Label>Cliente</Label>
                  <Select value={form.tenant_id} onValueChange={(v) => { setForm({ ...form, tenant_id: v, partecipanti: [] }); loadEmps(v); }}>
                    <SelectTrigger className="mt-1.5" data-testid="session-tenant"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                    <SelectContent>{organizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Pianificazione</Label>
                <Select value={form.pianificata ? "si" : "no"} onValueChange={(v) => setForm({ ...form, pianificata: v === "si" })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Pianificata</SelectItem>
                    <SelectItem value="no">Non pianificata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Finalità</Label><Input value={form.finalita} onChange={(e) => setForm({ ...form, finalita: e.target.value })} className="mt-1.5" placeholder="es. Sensibilizzazione" /></div>
            <div><Label>Oggetto dell'attività formativa</Label><Input value={form.oggetto} data-testid="session-oggetto" onChange={(e) => setForm({ ...form, oggetto: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Esempi / contenuti</Label><Textarea value={form.esempi} onChange={(e) => setForm({ ...form, esempi: e.target.value })} className="mt-1.5" rows={2} /></div>
            <div><Label>Durata</Label><Input value={form.durata} onChange={(e) => setForm({ ...form, durata: e.target.value })} className="mt-1.5" placeholder="es. 30 minuti" /></div>

            <div>
              <Label>Tipo di attività</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {TIPI_ATTIVITA_FORMATIVA.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={(form.tipo_attivita || []).includes(t)} onCheckedChange={() => toggleArr("tipo_attivita", t)} />{t}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Destinatari</Label>
                <div className="flex flex-col gap-2 mt-2">
                  {DESTINATARI_FORMAZIONE.map((d) => (
                    <label key={d} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={(form.destinatari || []).includes(d)} onCheckedChange={() => toggleArr("destinatari", d)} />{d}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Riferimenti documentazione</Label>
                <Select value={form.riferimenti_tipo || "none"} onValueChange={(v) => setForm({ ...form, riferimenti_tipo: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {RIFERIMENTI_TIPO.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Docente (Cognome Nome)</Label><Input value={form.docente_nome} onChange={(e) => setForm({ ...form, docente_nome: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Mansione docente</Label><Input value={form.docente_mansione} onChange={(e) => setForm({ ...form, docente_mansione: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Data attività</Label><Input type="date" value={form.data || ""} data-testid="session-data" onChange={(e) => setForm({ ...form, data: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Scadenza aggiornamento <span className="text-slate-400 font-normal">(comparirà in Agenda Scadenze)</span></Label><Input type="date" value={form.scadenza_aggiornamento || ""} data-testid="session-scadenza" onChange={(e) => setForm({ ...form, scadenza_aggiornamento: e.target.value })} className="mt-1.5" /></div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <Label>Partecipanti</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Select value="" onValueChange={addEmployee}>
                  <SelectTrigger className="flex-1" data-testid="session-add-employee"><SelectValue placeholder="Aggiungi da dipendenti…" /></SelectTrigger>
                  <SelectContent>
                    {emps.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Nessun dipendente per questo cliente</div>}
                    {emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}{e.ruolo ? ` — ${e.ruolo}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input value={manual.nome} onChange={(e) => setManual({ ...manual, nome: e.target.value })} placeholder="Nome (aggiungi a mano)" className="h-9" />
                <Input value={manual.mansione} onChange={(e) => setManual({ ...manual, mansione: e.target.value })} placeholder="Mansione" className="h-9" />
                <Button variant="outline" size="sm" onClick={addManual} data-testid="session-add-manual"><Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi</Button>
              </div>
              {(form.partecipanti || []).length > 0 && (
                <div className="space-y-1">
                  {form.partecipanti.map((p, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 rounded bg-slate-50 px-3 py-1.5 text-sm">
                      <span>{p.nome}{p.mansione ? <span className="text-slate-500"> — {p.mansione}</span> : ""}{p.employee_id ? <Badge variant="secondary" className="ml-2 text-[10px]">dipendente</Badge> : null}</span>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => removePart(i)}><Trash size={13} /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div><Label>Responsabile esecuzione</Label><Input value={form.responsabile_esecuzione} onChange={(e) => setForm({ ...form, responsabile_esecuzione: e.target.value })} className="mt-1.5" /></div>

            <div className="rounded-lg border border-slate-200 p-3 space-y-3">
              <Label>Verifica efficacia attività formativa</Label>
              <div className="flex flex-wrap gap-4">
                {METODI_VERIFICA_EFFICACIA.map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={(form.verifica_efficacia || []).includes(m)} onCheckedChange={() => toggleArr("verifica_efficacia", m)} />{m}
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <Label>Esito</Label>
                  <Select value={form.esito_verifica || "none"} onValueChange={(v) => setForm({ ...form, esito_verifica: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1.5" data-testid="session-esito"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {ESITI_VERIFICA.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.esito_verifica === "VNC" && (
                  <div><Label>VNC n°</Label><Input value={form.vnc_numero} onChange={(e) => setForm({ ...form, vnc_numero: e.target.value })} className="mt-1.5" /></div>
                )}
                <div><Label>Data verifica</Label><Input type="date" value={form.data_verifica || ""} onChange={(e) => setForm({ ...form, data_verifica: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Valutatore</Label><Input value={form.valutatore} onChange={(e) => setForm({ ...form, valutatore: e.target.value })} className="mt-1.5" /></div>
              </div>
            </div>

            <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-session-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva scheda</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
